import { AfterViewInit, Component, ElementRef } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { TranslateService } from '@ngx-translate/core';
import { Destroyable, takeUntilDestroy } from '@frontend-site/shared/utils';
import { filter } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonGridStandardRowHeightService } from '../../select-row-height/common-grid-standard-row-height.service';

@Component({
    selector: 'p-step-dependencies-renderer',
    template: `
        <div *ngIf="defaultDependency" class="flex-none common-grid-standard-row-height">
            {{ defaultTranslateKey | translate }}
        </div>
        <div *ngIf="hasNoDependencies" class="disabled flex-none common-grid-standard-row-height">
            {{ 'Execution.MainTabStrip.Steps.Grid.noDependency' | translate }}
        </div>
        <p-chip-group
            *ngIf="hasValue"
            class="p-step-dependencies-chips"
            pAddEllipsisIfOverflow
            [list]="value"
            [displayRemoveIcon]="false"
            [tabIndex]="0"
        ></p-chip-group>
    `,
    styleUrls: ['dependencies-renderer.component.scss']
})
export class DependenciesRendererComponent extends Destroyable implements ICellRendererAngularComp, AfterViewInit {
    value: string[] = [];
    protected hasNoDependencies!: boolean;
    protected defaultDependency!: boolean;
    protected defaultTranslateKey!: string;

    private prefixTranslateKey = '';

    constructor(
        private readonly translate: TranslateService,
        private readonly commonGridStandardRowHeightService: CommonGridStandardRowHeightService,
        private readonly el: ElementRef
    ) {
        super();
    }

    agInit(params: DependenciesRendererParams): void {
        this.defaultTranslateKey = params.defaultTranslateKey;
        this.prefixTranslateKey = params.prefixTranslateKey;
        this.setValue(params);

        this.hasNoDependencies = params.disabled ? params.disabled(params) && !!this.defaultTranslateKey : !!this.defaultDependency;
        this.defaultDependency = !!params.defaultTranslateKey && !this.value.length && !this.hasNoDependencies;
    }

    ngAfterViewInit(): void {
        this.commonGridStandardRowHeightService.rowHeight$
            .pipe(
                takeUntilDestroy(this),
                filter(Boolean),
                map((commonGridStandardRowHeight) => commonGridStandardRowHeight.height)
            )
            .subscribe((height) => {
                this.el.nativeElement.style.height = height + 'px';
            });
    }

    refresh(params: DependenciesRendererParams): boolean {
        this.setValue(params);
        return true;
    }

    private setValue(params: DependenciesRendererParams): void {
        const prefix = !!this.prefixTranslateKey ? this.translate.instant(this.prefixTranslateKey) : '';
        const rawValue: number[] = params.getValue?.() ?? [];
        this.value = rawValue.map((val) =>
            val == -1 ? this.translate.instant('Execution.MainTabStrip.Steps.Grid.noDependency') : `${prefix} ${val}`
        );
    }

    protected get hasValue(): boolean {
        return !!this.value?.length && !this.hasNoDependencies && !this.defaultDependency;
    }
}

export type DependenciesRendererParams = ICellRendererParams & {
    disabled: (data: ICellRendererParams) => boolean;
    defaultTranslateKey: string;
    prefixTranslateKey: string;
};
